import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  Wallet, Plus, RefreshCw, CheckCircle, XCircle, AlertCircle, Send,
  Calculator, Users, DollarSign, Calendar, MoreHorizontal, Eye, Play } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  calculating: 'bg-blue-500/20 text-blue-400',
  calculated: 'bg-cyan-500/20 text-cyan-400',
  in_review: 'bg-amber-500/20 text-amber-400',
  approved: 'bg-green-500/20 text-green-400',
  posted: 'bg-purple-500/20 text-purple-400',
  cancelled: 'bg-red-500/20 text-red-400'
};

export const PayrollPage = () => {const { t } = useTranslation("hr");
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [formData, setFormData] = useState({
    period_code: '', period_start: '', period_end: '', pay_date: '',
    payroll_type: 'regular', notes: ''
  });

  const fetchRuns = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/hr/payroll-runs?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch payroll runs');
      const data = await res.json();
      // API returns array directly
      setRuns(Array.isArray(data) ? data : data.runs || []);
    } catch (err) {
      toast.error(err.message);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {fetchRuns();}, [statusFilter]);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/payroll-runs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create payroll run');
      }
      toast.success('Payroll run created');
      setShowCreate(false);
      setFormData({ period_code: '', period_start: '', period_end: '', pay_date: '', payroll_type: 'regular', notes: '' });
      fetchRuns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCalculate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/payroll-runs/${id}/calculate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to calculate payroll');
      toast.success('Payroll calculated successfully');
      fetchRuns();
    } catch (err) {toast.error(err.message);}
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/payroll-runs/${id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to approve payroll');
      toast.success('Payroll approved');
      fetchRuns();
    } catch (err) {toast.error(err.message);}
  };

  const handlePost = async (id) => {
    if (!window.confirm('Post this payroll to accounting? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/hr/payroll-runs/${id}/post`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to post payroll');
      toast.success('Payroll posted to accounting');
      fetchRuns();
    } catch (err) {toast.error(err.message);}
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/payroll-runs/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch payroll details');
      const data = await res.json();
      setShowDetails(data);
    } catch (err) {toast.error(err.message);}
  };

  const totalGross = runs.reduce((sum, r) => sum + (r.total_gross || 0), 0);
  const totalNet = runs.reduce((sum, r) => sum + (r.total_net || 0), 0);
  const postedCount = runs.filter((r) => r.status === 'posted').length;

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("payroll", "Payroll")}</h1>
          <p className="text-muted-foreground mt-1">{t("processAndManage", "Process and manage employee payroll")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("newPayrollRun", "New Payroll Run")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalRuns", "Total Runs")}</p>
                <p className="text-3xl font-bold">{runs.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><Wallet className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("posted", "Posted")}</p>
                <p className="text-3xl font-bold">{postedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20"><Send className="h-6 w-6 text-purple-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalGross", "Total Gross")}</p>
                <p className="text-3xl font-bold">${totalGross.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><DollarSign className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalNet", "Total Net")}</p>
                <p className="text-3xl font-bold">${totalNet.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/20"><DollarSign className="h-6 w-6 text-amber-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payroll Runs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t("payrollRuns", "Payroll Runs")}</CardTitle>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                  <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                  <SelectItem value="calculated">{t("calculated", "Calculated")}</SelectItem>
                  <SelectItem value="approved">{t("approved", "Approved")}</SelectItem>
                  <SelectItem value="posted">{t("posted", "Posted")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchRuns}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          runs.length === 0 ?
          <div className="text-center py-12">
              <Wallet className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noPayrollRuns", "No payroll runs found")}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />{t("createFirstRun", "Create First Run")}
            </Button>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("period", "Period")}</th>
                    <th className="text-left p-3 font-medium">{t("type", "Type")}</th>
                    <th className="text-left p-3 font-medium">{t("employees", "Employees")}</th>
                    <th className="text-left p-3 font-medium">{t("grossPay", "Gross Pay")}</th>
                    <th className="text-left p-3 font-medium">{t("netPay", "Net Pay")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {runs.map((run) =>
                <tr key={run.id} className="hover:bg-slate-800/30">
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{run.period_code}</p>
                          <p className="text-sm text-muted-foreground">{t("payDate", "Pay date:")}{run.pay_date}</p>
                        </div>
                      </td>
                      <td className="p-3 capitalize">{run.payroll_type}</td>
                      <td className="p-3">{run.employee_count || 0}</td>
                      <td className="p-3 font-medium">${(run.total_gross || 0).toLocaleString()}</td>
                      <td className="p-3 font-medium text-green-400">${(run.total_net || 0).toLocaleString()}</td>
                      <td className="p-3">
                        <Badge className={statusColors[run.status]}>{run.status}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetails(run.id)}>
                              <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                        </DropdownMenuItem>
                            {run.status === 'draft' &&
                        <DropdownMenuItem onClick={() => handleCalculate(run.id)}>
                                <Calculator className="h-4 w-4 mr-2" />{t("calculate", "Calculate")}
                        </DropdownMenuItem>
                        }
                            {(run.status === 'calculated' || run.status === 'in_review') &&
                        <DropdownMenuItem onClick={() => handleApprove(run.id)}>
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />{t("approve", "Approve")}
                        </DropdownMenuItem>
                        }
                            {run.status === 'approved' &&
                        <DropdownMenuItem onClick={() => handlePost(run.id)}>
                                <Send className="h-4 w-4 mr-2 text-purple-500" />{t("postToAccounting", "Post to Accounting")}
                        </DropdownMenuItem>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("newPayrollRun", "New Payroll Run")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("periodCode", "Period Code *")}</Label>
                <Input value={formData.period_code} onChange={(e) => setFormData({ ...formData, period_code: e.target.value })} placeholder="2026-02" />
              </div>
              <div>
                <Label>{t("payrollType", "Payroll Type")}</Label>
                <Select value={formData.payroll_type} onValueChange={(v) => setFormData({ ...formData, payroll_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">{t("regular", "Regular")}</SelectItem>
                    <SelectItem value="bonus">{t("bonus", "Bonus")}</SelectItem>
                    <SelectItem value="thirteenth">{t("13thSalary", "13th Salary")}</SelectItem>
                    <SelectItem value="adjustment">{t("adjustment", "Adjustment")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("periodStart", "Period Start *")}</Label>
                <Input type="date" value={formData.period_start} onChange={(e) => setFormData({ ...formData, period_start: e.target.value })} />
              </div>
              <div>
                <Label>{t("periodEnd", "Period End *")}</Label>
                <Input type="date" value={formData.period_end} onChange={(e) => setFormData({ ...formData, period_end: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("payDate", "Pay Date *")}</Label>
              <Input type="date" value={formData.pay_date} onChange={(e) => setFormData({ ...formData, pay_date: e.target.value })} />
            </div>
            <div>
              <Label>{t("notes", "Notes")}</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createRun", "Create Run")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("payrollRunDetails", "Payroll Run Details")}</DialogTitle>
          </DialogHeader>
          {showDetails &&
          <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("period", "Period")}</p>
                  <p className="font-bold">{showDetails.period_code}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                  <Badge className={statusColors[showDetails.status]}>{showDetails.status}</Badge>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("employees", "Employees")}</p>
                  <p className="font-bold">{showDetails.employee_count || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("payDate", "Pay Date")}</p>
                  <p className="font-bold">{showDetails.pay_date}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-muted-foreground">{t("totalGross", "Total Gross")}</p>
                  <p className="text-2xl font-bold text-green-400">${(showDetails.total_gross || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm text-muted-foreground">{t("totalDeductions", "Total Deductions")}</p>
                  <p className="text-2xl font-bold text-red-400">${(showDetails.total_deductions || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-sm text-muted-foreground">{t("totalNet", "Total Net")}</p>
                  <p className="text-2xl font-bold text-blue-400">${(showDetails.total_net || 0).toLocaleString()}</p>
                </div>
              </div>
              {showDetails.lines && showDetails.lines.length > 0 &&
            <div>
                  <h4 className="font-semibold mb-2">{t("employeeBreakdown", "Employee Breakdown")}</h4>
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">{t("employee", "Employee")}</th>
                          <th className="text-right p-2">{t("gross", "Gross")}</th>
                          <th className="text-right p-2">{t("deductions", "Deductions")}</th>
                          <th className="text-right p-2">{t("net", "Net")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {showDetails.lines.map((line, idx) =>
                    <tr key={idx}>
                            <td className="p-2">{line.employee_name || `Employee #${line.employee_id}`}</td>
                            <td className="p-2 text-right">${(line.gross_pay || 0).toLocaleString()}</td>
                            <td className="p-2 text-right text-red-400">${(line.total_deductions || 0).toLocaleString()}</td>
                            <td className="p-2 text-right text-green-400">${(line.net_pay || 0).toLocaleString()}</td>
                          </tr>
                    )}
                      </tbody>
                    </table>
                  </div>
                </div>
            }
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(null)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default PayrollPage;